import React, { useContext, useMemo } from 'react';
import { Box, Typography, Stack, Chip } from '@mui/material';
import { DateTime } from 'luxon';
import { CacheContext, CacheContextType } from '../context/CacheContext';
import Post from '../postComponent/postComponent';
import { PostData } from '../model/types';

/**
 * Death Row — posts expiring in < 1 hour that need votes to survive.
 * An ephemerality-first feature: gives the community a chance to rescue content.
 */
function DeathRow() {
  const allPosts = useContext<CacheContextType>(CacheContext).allPosts;

  const dyingPosts = useMemo(() => {
    const now = DateTime.now();
    const oneHourFromNow = now.plus({ hours: 1 });
    return allPosts()
      .filter((p: PostData) => {
        if (p.is_expired || p.is_permanent) return false;
        const expiry = DateTime.fromISO(p.expire_at);
        return expiry > now && expiry <= oneHourFromNow;
      })
      .sort((a: PostData, b: PostData) =>
        DateTime.fromISO(a.expire_at).toSeconds() - DateTime.fromISO(b.expire_at).toSeconds()
      )
      .slice(0, 5);
  }, [allPosts]); // eslint-disable-line react-hooks/exhaustive-deps

  if (dyingPosts.length === 0) return null;

  return (
    <Box
      sx={{
        mb: 3,
        border: '1px solid',
        borderColor: 'error.main',
        borderRadius: '8px',
        overflow: 'hidden',
        animation: 'deathRowPulse 3s ease-in-out infinite',
        '@keyframes deathRowPulse': {
          '0%, 100%': { borderColor: '#b51e1b' },
          '50%': { borderColor: 'rgba(181,30,27,0.35)' },
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          bgcolor: 'error.main',
          px: 2,
          py: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            sx={{
              fontFamily: '"VT323", monospace',
              fontSize: '1.1rem',
              letterSpacing: 2,
              color: 'white',
              lineHeight: 1,
            }}
          >
            ⚠ DEATH ROW
          </Typography>
          <Chip
            label={`${dyingPosts.length} post${dyingPosts.length !== 1 ? 's' : ''} expiring`}
            size="small"
            sx={{
              bgcolor: 'rgba(255,255,255,0.2)',
              color: 'white',
              fontSize: '0.7rem',
              height: 20,
              '& .MuiChip-label': { px: 1 },
            }}
          />
        </Box>
        <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.8)' }}>
          vote to keep them alive
        </Typography>
      </Box>

      {/* Posts */}
      <Stack spacing={0} divider={<Box sx={{ borderTop: '1px solid', borderColor: 'divider' }} />}>
        {dyingPosts.map((post: PostData) => {
          const remaining = DateTime.fromISO(post.expire_at).diff(DateTime.now()).as('minutes');
          return (
            <Box key={post.id} sx={{ position: 'relative' }}>
              {/* Urgency bar showing time remaining */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  height: '2px',
                  width: `${Math.max(1, Math.min(100, (remaining / 60) * 100))}%`,
                  bgcolor: 'error.main',
                  transition: 'width 60s linear',
                  zIndex: 1,
                }}
              />
              <Box sx={{ px: 0, pt: 0.5 }}>
                <Post post={post} />
              </Box>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}

export default DeathRow;
